import QueryBuilder, {QueryBuilderUtils} from "../../../../../../packages/query-builder";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useDynamicColumns, {DynamicColumnApplications} from "../../../../../hooks/use-dynamic-columns";
import {useDataGridSavedState} from "../../../../../../packages/data-grid";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useSearch from "../../../../../hooks/use-search";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {createUUId, exportCSVFile} from "../../../../../../core/services/utils/utils";
import SortIcon from '@mui/icons-material/Sort';
import GridViewIcon from '@mui/icons-material/GridView';
import Grid from '@mui/material/Unstable_Grid2';
import Button from "../../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import NoteGridView from "./views/grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import NoteListView from "./views/list";
import useReportingSearchUtils from "../../../../../hooks/use-reporting-search-utils";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import Api from "../../../../../../core/services/api/api";
import notebookTags, {NotebookTagKeys} from "../../../../../../core/models/static/routes/notebook-tags";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import DateUtils from "../../../../../../core/services/date-utils";
import ToastService from "../../../../../../core/services/toast";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const queryBuilderSaveName = 'NCRNotesListViewQueryBuilder';
const dynamicColumnsSectionName = 'Note';
const dataGridSaveConfig = {
    name: "NCRNotesListViewDataGrid",
    version: 2,
};

const knownColumnNames = {
    ncrCode: 'ncrCode',
    title: 'title',
    submittedByName: 'submittedByName',
    submitDate: 'submitDate',
    content: 'content',
}


const NCRNotes = () => {
    const [data, setData] = useState([]);
    const [gridView, setGridView] = useState(true);
    const {configureNotebookTags} = useConfigureNotebook('ncr-notes');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [noteTitleSuggestions, setNoteTitleSuggestions] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {packages} = useAccessLevels();
    const canExportNotes = packages.ncr.exportNcrNotes;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.ncrNotes);
        configureBehavioralTool((tags) => tags.ncrNotes);
    }, [configureNotebookTags]);

    const dataGridId = useMemo(() => createUUId(true), []);

    /**
     * Fetches the title suggestions for the notes.
     * @type {(function(): Promise<void>)|*}
     */
    const getTitleSuggestions = useCallback(async () => {
        const response = await Api.getNcrNoteTitleSuggestions();
        if (!isMounted())
            return
        setNoteTitleSuggestions(response?.data ?? []);
    }, [isMounted])

    /**
     * As soon as the component is mounted:
     * - fetches the title suggestions for the notes.
     */
    useEffect(() => {
        void getTitleSuggestions();
    }, [getTitleSuggestions])

    /**
     * Changes the view between grid and list view
     * @type {(function(*): void)|*}
     */
    const changeView = useCallback((view) => {
        if (view === 'list') {
            setGridView(false)
        } else {
            setGridView(true);
        }
    }, []);


    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback((theme) => {
        return ({
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.palette.text.primary,
                height: 30,
                marginBlock: 0.5,
                marginInline: 0.5,
                borderRadius: 1,
                paddingBlock: 0.5,
                paddingInline: 0.5,
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
            },
        })
    }, []);


    const dataGridColumnsConfig = useMemo(() => ({
        [knownColumnNames.ncrCode]: {
            width: 80,
            pinned: true,
        },
        [knownColumnNames.content]: {
            width: 400,
        },
        [knownColumnNames.title]: {
            width: 100,
        },
        [knownColumnNames.submitDate]: {
            width: 60,
        },
        [knownColumnNames.submittedByName]: {
            width: 240,
        },
    }), []);


    const [dynamicColumnsLoading, queryBuilderColumns, dataGridColumns] = useDynamicColumns({
        sectionName: dynamicColumnsSectionName,
        dataGridColumnsConfig: dataGridColumnsConfig,
        applicationName: DynamicColumnApplications.BizNoteService,
    });


    const [savedDataGridColumns, dataGridSortBy, dataGridPageSize,] = useDataGridSavedState({
        columns: dataGridColumns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    /**
     * Fetches the entries from the server based on the user's filters.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const siftedFilters = QueryBuilderUtils.getApiQueryFromStateQuery(args.filters, queryBuilderColumns);
        const forApi = {
            keyword: siftedFilters.keyword?.length ? siftedFilters.keyword : null,
            tags: notebookTags.ncrInformation(),
            pagination: args.paginationInfo,
            sorts: args.orderBy ? [{isDesc: args.orderBy.descending, Property: args.orderBy.field,}] : null,
        }
        const response = await Api.getPageNotes(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            if (gridView) {
                setData(prevState => [...prevState, ...response.data?.items ?? []])
            } else {
                setData(response.data?.items ?? []);
            }
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, queryBuilderColumns, gridView]);

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback(async (args) => {
        await fetchEntries(args, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    const hasMoreNotesToLoad = useMemo(() => {
        return !searchData.loading && searchData.paginationInfo &&
            searchData.paginationInfo.length > searchData.paginationInfo.pageSize * searchData.paginationInfo.currentPage
    }, [searchData.paginationInfo, searchData.loading])


    /**
     * Handles loading more data based on the infinite scroll:
     * sets the system loading and calls the callback passed from the useSearchData hook if limitations passed
     */
    const loadMoreData = async () => {
        if (!data.length || searchData.loading)
            return;
        searchData.setLoading(true);
        searchData.setPaginationInfo(prevState => ({...prevState, currentPage: prevState.currentPage + 1}))
    }

    /**
     * When the filters change, the data should be reset.
     */
    useEffect(() => {
        setData([]);
    }, [searchData.filters])


    /**
     * Removes the given note from the notes of the NCR's in the server.
     * @param noteOrNotes
     * @return {Promise<void>}
     */
    const removeNote = useCallback(async (noteOrNotes) => {
        let noteIds;
        if (Array.isArray(noteOrNotes)) {
            noteIds = noteOrNotes.map(note => note.id);
        } else {
            noteIds = [noteOrNotes.id];
        }
        const promises = [];
        for (const noteId of noteIds) {
            promises.push(new Promise(async (resolve) => {
                const response = await Api.removeNote(noteId)
                resolve([noteId, response]);
            }));
        }
        await Promise.all(promises);
        if (!isMounted())
            return;
        for (const [noteId, response] of promises) {
            if (response?.resultFlag) {
                setData(prevState => prevState.filter(e => e.id !== noteId));
            }
        }
    }, [isMounted])

    /**
     * Opens the upsert dialog with the given note.
     * @type {(function(*): void)|*}
     */
    const openUpsertDialog = useCallback((note) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: note,
            titleSuggestions: noteTitleSuggestions.map(e => e.title),
            onSuccess: (note) => setData(prevState => prevState.map(e => e.id === note.id ? note : e)),
        }))
    }, [dispatch, noteTitleSuggestions])

    /**
     * Formats the exported cell based on the column type.
     */
    const getDataGridCell = useCallback((row, column, continueMarker) => {
        switch (column.name) {
            default:
                return continueMarker
        }
    }, []);

    const {getAllData} = useReportingSearchUtils({
        savedDataGridColumns,
        fetchEntries,
        searchData,
        getDataGridCell,
        data,
    });

    /**
     * Builds CSV export payload for NCR notes (includes HTML body).
     */
    const buildExportInfo = useCallback(async () => {
        const allData = await getAllData();
        if (!isMounted())
            return;
        if (!allData?.length) {
            ToastService.warning({title: "No Data Found", message: 'There are no notes to export.'});
            return;
        }
        return {
            title: "NCR Notes".concat(' - Exported Data').concat(` - ${DateUtils.localNow().format(DateUtils.dateTimeFormat)}`),
            body: allData.map(e => ({
                "Title": e.title,
                "Message": e.body,
                "Submitted By": e.createdByName,
                "Modified Date": e.modifiedDateTime ? DateUtils.serverToLocal(e.modifiedDateTime).format(DateUtils.dateTimeFormat) : '--',
                "NCR Code": e.tagList?.find(e => e.key === NotebookTagKeys.ncrCode)?.value,
            })),
        };
    }, [getAllData, isMounted]);

    /**
     * Exports NCR notes to CSV (header button).
     */
    const exportData = useCallback(async () => {
        const exportInfo = await buildExportInfo();
        if (!exportInfo?.body?.length) {
            return;
        }
        exportCSVFile(exportInfo.body, exportInfo.title);
    }, [buildExportInfo]);


    return (
        <>
            <Grid container spacing={2} className={'ncr-notes-list'}>
                <Grid container columns={12} xs={12}>
                    <Grid xs className={'view-title'}>
                        <p>Notes List</p>
                    </Grid>
                    <Grid xs={'auto'} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2}}>
                        {
                            canExportNotes &&
                            <Button
                                className={"mr-2 d-flex align-items-center justify-content-center"}
                                variant={"outlined"}
                                color={"primary"}
                                onClick={exportData}
                            >
                                <Add className={`icon small`} style={{margin: "2px 0"}}/>
                                <p className="ml-2">Export to CSV</p>
                            </Button>
                        }
                        <ToggleButtonGroup
                            spacing={2}
                            className={'button-group'}
                            orientation="horizontal"
                            exclusive
                            onChange={(e, value) => changeView(value)}
                            sx={toggleButtonGroupMixin}
                        >
                            <ToggleButton
                                selected={!gridView}
                                value={'list'}
                                className={'mr-1'}
                                style={{borderRadius: 5}}>
                                <SortIcon/>
                            </ToggleButton>
                            <ToggleButton
                                selected={gridView}
                                value={'grid'}
                                style={{borderRadius: 5}}>
                                <GridViewIcon/>
                            </ToggleButton>

                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
                {
                    dynamicColumnsLoading &&
                    <>
                        <LoadingLottieAnimation
                            width={'50%'}
                            className={'my-5'}
                        />
                    </>
                }
                {
                    !dynamicColumnsLoading &&
                    <>
                        <Grid xs={12}>
                            <QueryBuilder
                                columns={queryBuilderColumns}
                                name={queryBuilderSaveName}
                                search={searchData.setFilters}
                                query={searchData.filters}
                                loading={searchData.loading}
                                dataGridId={dataGridId}
                                hideConditions
                            />

                        </Grid>
                        <Grid xs={12}>
                            {
                                gridView &&
                                <NoteGridView
                                    hasMoreNotesToLoad={hasMoreNotesToLoad}
                                    loadMoreData={loadMoreData}
                                    notes={data}
                                    searchData={searchData}
                                    updateNote={openUpsertDialog}
                                    removeNote={removeNote}
                                />
                            }
                            {
                                !gridView &&
                                <NoteListView
                                    searchData={searchData}
                                    notes={data}
                                    updateNote={openUpsertDialog}
                                    removeNote={removeNote}
                                    exportInfo={canExportNotes ? buildExportInfo : undefined}
                                />
                            }
                        </Grid>
                    </>
                }
            </Grid>
        </>
    )
};

export default NCRNotes;
